import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquare, ShieldCheck, CornerDownRight } from "lucide-react";

function groupByThread(items) {
  const roots = items.filter(c => !c.parent_id);
  const replies = items.filter(c => c.parent_id);
  return roots.map(r => ({ ...r, replies: replies.filter(x => x.parent_id === r.comment_id) }));
}

function CommentCard({ c, isReply = false }) {
  const admin = !!c.is_admin;
  return (
    <div className={`text-sm rounded-lg p-3 ${admin ? "bg-emerald-50 border border-emerald-300" : "bg-[#FAFA96]/40"} ${isReply ? "ml-6" : ""}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1 flex-wrap">
        {isReply && <CornerDownRight size={12} className="text-emerald-600"/>}
        <span className="font-semibold text-[#0F2922]">{c.user_name}</span>
        {admin ? (
          <Badge className="bg-emerald-600 text-white border-0 text-[10px]"><ShieldCheck size={10} className="mr-0.5"/> Admin</Badge>
        ) : (
          <span className="px-1.5 py-0.5 rounded bg-[#0F2922] text-[#FAFA96] text-[10px]">{c.user_jenjang}</span>
        )}
        <span>· {new Date(c.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <div className="text-slate-700 whitespace-pre-line">{c.text}</div>
    </div>
  );
}

export default function Comments({ contentType, contentId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get(`/comments/${contentType}/${contentId}`);
      setItems(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open, contentType, contentId]); // eslint-disable-line

  const submit = async () => {
    if (!text.trim()) return;
    try {
      await http.post("/comments", { content_type: contentType, content_id: contentId, text });
      setText("");
      await load();
      toast.success("Komentar ditambahkan");
    } catch { toast.error("Gagal mengirim komentar"); }
  };

  const threads = groupByThread(items);

  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-xs text-slate-500 hover:text-[#0F2922]" data-testid={`comments-toggle-${contentId}`}>
        <MessageSquare size={14} /> {open ? "Sembunyikan" : "Diskusi"} ({items.length})
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {user && (
            <div className="flex gap-2">
              <Textarea placeholder="Tulis komentar…" value={text} onChange={e=>setText(e.target.value)} className="min-h-[60px] text-sm" data-testid={`comments-input-${contentId}`} />
              <Button onClick={submit} className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96] self-end" data-testid={`comments-submit-${contentId}`}>Kirim</Button>
            </div>
          )}
          <div className="space-y-2 max-h-96 overflow-auto">
            {threads.map(t => (
              <div key={t.comment_id} className="space-y-2">
                <CommentCard c={t} />
                {t.replies.map(r => <CommentCard key={r.comment_id} c={r} isReply />)}
              </div>
            ))}
            {threads.length === 0 && <div className="text-xs text-slate-400 italic">Belum ada diskusi. Jadilah yang pertama!</div>}
          </div>
        </div>
      )}
    </div>
  );
}
